import React, {MutableRefObject, useImperativeHandle, useState} from "react";
import {arrayPush, arrayRemoveIndex, arrayUpdate, arrayUpdatePartial, ArrayUpdateType} from "../../immutableState";
import CheckBox from "../../components/CheckBox";
import SelectNumber from "../../components/SelectNumber";
import {classNames} from "../../wrapper";
import Input from "../../components/Input";
import TestType from "../../controllers/TestType";
import PrepareSubmissionLeaf from "../../controllers/PrepareSubmissionLeaf";
import SubmissionDataResponse from "../../controllers/SubmissionDataResponse";
import Cultivar from "../../controllers/Cultivar";
import {useValidationArray} from "../validationArray";
import OrganType from "../../controllers/OrganType";
import SelectNumberNullable from "../../components/SelectNumberNullable";
import GenericSelect from "../../components/GenericSelect";

export function getLabel(t: OrganType): string {
    switch (t) {
        case OrganType.Vegetative:
            return "ITEST Leaf (Vegetative)";
        case OrganType.Reproductive:
            return "ITEST Leaf (Reproductive)";
        case OrganType.Normal:
            return "ITEST Leaf";
        default:
            return "None";
    }
}

interface LeafRow extends PrepareSubmissionLeaf {
    selected: boolean;
}

function emptyLeafRow(): LeafRow {
    return {
        selected: false,
        test: TestType.StandardItestLeaf,
        cultivars: [],
        growthStage: "",
        optionalTests: null,
        testId: 0,
        testDataId: null,
        submissionId: null,
        blockNumber: "",
        sampleNumber: "",
        notes: "",
        cropId: 0,
        waypointCropCode: '',
        delete: false,
        agronomist: "",
        farmName: "",
        depot: "",
        clientName: "",
        agent: "",
        appAdded: new Date(),
        rawLab: "",
        organTypeEnum: OrganType.None
    }
}

export interface SubmissionPrepareFunctions {
    validate: () => boolean;
}


const SubmissionTable: React.FC<{
    cultivars: Record<number, Cultivar[]>
    leafs: LeafRow[]
    data: SubmissionDataResponse<PrepareSubmissionLeaf>
    setData: (data: LeafRow[]) => void
    index: number
    submitRef?: MutableRefObject<SubmissionPrepareFunctions | undefined>;
}> = (props) => {
    
    const [headerSampleType, setHeaderSampleType] = useState<OrganType>(OrganType.None);
    const [headerCrop, setHeaderCrop] = useState(0);
    const [headerCultivar, setHeaderCultivar] = useState<number[]>([]);
    const [headerTest, setHeaderTest] = useState<TestType>(TestType.StandardItestLeaf);
    const [headerOptionalTests, setHeaderOptionalTests] = useState<TestType[]>([]);
    const [split , setSplit] = useState<boolean>(false)
    
    useImperativeHandle(props.submitRef, () => ({
        validate: () => validation.validate()
    }));
    
    function lengthCheck(length: number, deleted: boolean) {
        // if it's deleted, it's fine and the validation should pass
        return length > 0 || deleted;
    }

    function duplicatesampleNumberCheck(sampleNumber: string) {
        return props.leafs.filter(l => l.sampleNumber === sampleNumber).length === 1;
    }

    const validation = useValidationArray(props.leafs)({
        sampleNumber: leaf => lengthCheck(leaf.sampleNumber.length, leaf.delete) && duplicatesampleNumberCheck(leaf.sampleNumber),
        block: leaf => lengthCheck(leaf.blockNumber.length, leaf.delete),
        crop: leaf => lengthCheck(leaf.cropId, leaf.delete),
        cultivar: leaf => lengthCheck(leaf.cultivars.length, leaf.delete),
        growthPhase: leaf => lengthCheck(leaf.growthStage.length, leaf.delete),
        organType: leaf => leaf.organTypeEnum !== OrganType.None || leaf.delete,
        test: leaf => (leaf.test != null && leaf.test != TestType.Unknown) || leaf.delete
    })
    
    function setLeafDataIndex(index: number, update: ArrayUpdateType<LeafRow>) {
        props.setData(arrayUpdatePartial( props.leafs, index, update))
    }
    
    function setLeafData(update: (leaf: LeafRow) => LeafRow) {
        const leafs = props.leafs;
        props.setData(leafs.map((l, i) => {
            return update(l)
        }))
    }

    function addCultivar(leaf: LeafRow, leafIndex: number): void {
        // select the first one from the list
        const list = props.cultivars[leaf.cropId] ?? [];
        if (list.length > 0)
            setLeafDataIndex(leafIndex, {cultivars: arrayPush(leaf.cultivars, 0)});
    }

    function updateHeaderCrop(cropId: number) {
        setHeaderCrop(cropId);
        setHeaderCultivar([])

        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, cropId: cropId, cultivars: []};
        })
    }
    function updateHeader<T>(value: T, setHeader: (value: T) => void, property: keyof LeafRow) {
        setHeader(value);
        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, [property]: value};
        })
    }

    function changeHeaderTest(test: TestType) {
        updateHeader(test, setHeaderTest, 'test');
    }

    function updateHeaderSampleType(sampleDepth: OrganType) {
        updateHeader(sampleDepth, setHeaderSampleType, 'organTypeEnum');
    }

    function changeHeaderOptionalTest(optionalTests: TestType[]) {
        setHeaderOptionalTests(optionalTests);

        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, optionalTests: [...optionalTests]};
        })
    }

    function addHeaderCultivar() {
        // select the first one from the list
        const list = props.cultivars[headerCrop] ?? [];
        if (list.length > 0) {
            updateCultivarRows(arrayPush(headerCultivar, list[0]!.id))
        }
    }

    function changeHeaderCultivar(cultivarIndex: number, cultivarId: number) {
        updateCultivarRows(arrayUpdate(headerCultivar, cultivarIndex, cultivarId))
    }

    function removeHeaderCultivar(cultivarIndex: number) {
        updateCultivarRows(arrayRemoveIndex(headerCultivar, cultivarIndex))
    }

    function updateCultivarRows(cultivars: number[]) {
        setHeaderCultivar(cultivars)
        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, cultivars: cultivars};
        })
    }
    
    function changeCheckbox(index: number, v: boolean) {
        props.setData(props.leafs.map((l, i) => i===index ? {...l, selected: v} : l))
        if (!props.leafs.some(l => l.selected)) setSplit(false)
        setLeafDataIndex(index, {selected: v})
    }
    
    function updateSelectAll(v: boolean) {
        setLeafData(l => ({...l, selected: v}))
        props.setData(props.leafs.map(l => ({...l, selected: v})))
    }

    function pasteInColumn(e: React.ClipboardEvent<HTMLInputElement>, rowIndex: number, partial: (value: string) => Partial<LeafRow>) {
        const text = e.clipboardData.getData('text');
        const lines = text
            .trim()
            .split(text.includes('\n') ? '\n' : '\t')
            .map(t => t.replace('\r', ''));
        if (!text || text === '' || lines.length === 0) {
            return
        }
        
        const entries = new Array(Math.max(lines.length + rowIndex, props.leafs.length))
            .fill(null)
            .map<LeafRow>((_, index) => {
                const merge = props.leafs[index] ?? emptyLeafRow()

                const lineIndex = index - rowIndex;
                if (lineIndex >= 0 && lineIndex < lines.length) {
                    return {
                        ...merge,
                        ...partial(lines[lineIndex]!)
                    }
                }

                return merge
            });
        props.setData(entries)
        e.preventDefault()
    }

    function removeLeaf(leafs: LeafRow[], index: number) {
        // this is only to remove leafs where the test data id is null (so if it is a manual submission and did not come from the capture app)
        props.setData(arrayRemoveIndex(leafs, index))
    }

    return <div> 
        <table className="w-full">
        <thead>
        <tr className='bg-gray-100 '>
            <th className="p-2 text-xs align-bottom">
                <div className='text-center'>
                    <div>Select</div>
                    <div className="text-center">
                        <CheckBox checked={props.leafs.every(l => l.selected)}
                                  onChange={v => updateSelectAll(v)}/>
                    </div>
                </div>
            </th>
            <th className="p-2 text-xs align-bottom">Sample Number</th>
            <th className="p-2 text-xs align-bottom">
                <div>
                    Sample Type
                </div>
                <SelectNumberNullable options={Object.values(OrganType)}
                                      textFunc={t => getLabel(t)}
                                      valueFunc={v => Object.values(OrganType).indexOf(v)}
                                      value={Object.values(OrganType).indexOf(headerSampleType)}
                                      onChange={v => updateHeaderSampleType(Object.values(OrganType)[v ?? 0] as OrganType)}/>
            </th>
            <th className="p-2 text-xs align-bottom">Block</th>
            <th className="p-2 text-xs align-bottom">
                <div>Crop</div>
                <SelectNumber options={props.data.crops} textFunc={c => c.name} valueFunc={c => c.id}
                              value={headerCrop} onChange={v => updateHeaderCrop(v)}/>
            </th>
            <th className="p-2 text-xs align-bottom">
                <div>Cultivar</div>
                <div className="flex">
                    <div className="btn bg-primary my-1" onClick={() => addHeaderCultivar()}>
                        +
                    </div>
                    {headerCultivar.map((cultivar, cultivarIndex) => <div className="flex" key={cultivarIndex}>
                        <SelectNumber options={props.cultivars[headerCrop] ?? []}
                                      textFunc={c => c.name} valueFunc={c => c.id} value={cultivar}
                                      onChange={v => changeHeaderCultivar(cultivarIndex, v)}/>

                        <div className="m-1 text-red-500 cursor-pointer"
                             onClick={() => removeHeaderCultivar(cultivarIndex)}>
                            x
                        </div>
                    </div>)}
                </div>
            </th>
            <th className="p-2 text-xs align-bottom">Growth phase</th>
            <th className="p-2 text-xs align-bottom">
                <div>Tests</div>
                <GenericSelect
                    options={props.data.tests}
                    textFunc={c => c.value}
                    valueFunc={c => c.key}
                    value={headerTest}
                    onChange={v => changeHeaderTest(v as TestType)}/>
            </th>
            <th className="p-2 text-xs align-bottom">
                <div>Optional Tests</div>
                <GenericSelect
                    options={props.data.optionalTests}
                    textFunc={c => c.value}
                    valueFunc={c => c.key}
                    value={headerOptionalTests || []}
                    multiple={true}
                    onChange={v => {
                        changeHeaderOptionalTest(v as TestType[])
                    }}
                />
            </th>
            <th className="text-xs align-bottom text-center">Actions</th>
        </tr>
        </thead>
        <tbody>
        {validation.items.map((leaf, index) =>
            <tr className={classNames(index % 2 === 0 ? '' : 'bg-gray-100', 'group hover:bg-gray-200 cursor-pointer')}
                key={index}>
                <td className={classNames("table-data text-center", leaf.data.delete ? 'inactive' : '')}>
                    <div className='flex-col items-end'>
                        <CheckBox checked={leaf.data.selected} onChange={v => changeCheckbox(index, v)}/>
                    </div>
                </td>
                <td className={classNames("table-data", leaf.data.delete ? 'inactive' : '')}>
                    <Input 
                        onPaste={e => pasteInColumn(e, index, v => ({sampleNumber: v}))}
                        className={leaf.rules.sampleNumber ? '' : 'border-1 border-red-600'} 
                        value={leaf.data.sampleNumber} 
                        change={v => setLeafDataIndex(index, {sampleNumber: v})}/>
                    {leaf.data.notes}
                </td>
                <td className={classNames("table-data", leaf.data.delete ? 'inactive' : '')}>
                    <SelectNumberNullable options={Object.values(OrganType)}
                                          className={leaf.rules.organType ? '' : 'border-1 border-red-600'}
                                          textFunc={t => getLabel(t)}
                                          valueFunc={v => Object.values(OrganType).indexOf(v)}
                                          value={Object.values(OrganType).indexOf(leaf.data.organTypeEnum)}
                                          onChange={v => setLeafDataIndex(index, { organTypeEnum: Object.values(OrganType)[v ?? 0] })}/>
                </td>
                <td className={classNames("table-data", leaf.data.delete ? 'inactive' : '')}>
                    <Input
                        onPaste={e => pasteInColumn(e, index, v => ({blockNumber: v}))}
                        className={leaf.rules.block ? '' : 'border-1 border-red-600'}
                        value={leaf.data.blockNumber} 
                        change={v => setLeafDataIndex(index, {blockNumber: v})}/>
                </td>
                <td className={classNames("table-data", leaf.data.delete ? 'inactive' : '')}>
                    <SelectNumber
                        className={leaf.rules.crop  ? '' : 'border-1 border-red-600'}
                        options={props.data.crops} 
                        textFunc={c => c.name} 
                        valueFunc={c => c.id}
                        value={leaf.data.cropId} 
                        onChange={v => setLeafDataIndex(index, {cropId: v})}/>
                </td>
                <td className={classNames("table-data", leaf.data.delete ? 'inactive' : '')}>
                    <div className={"flex"}>
                        <div
                            className={classNames('btn my-1', leaf.rules.cultivar  ? 'bg-primary' : 'border-1 bg-red-600')}
                            onClick={() => addCultivar(leaf.data, index)}>
                            +
                        </div>
                        {leaf.data.cultivars.map((cultivar, cultivarIndex) => <div className="flex items-center" key={cultivarIndex}>
                            <SelectNumber options={props.cultivars[leaf.data.cropId] ?? []}
                                          className={cultivar > 0 ? '' : 'border-1 border-red-600'}
                                          textFunc={c => c.name} valueFunc={c => c.id}
                                          value={cultivar}
                                          onChange={v => setLeafDataIndex(index, {cultivars: arrayUpdate(leaf.data.cultivars, cultivarIndex, v)})}/>

                            <div className="m-1 text-red-500 cursor-pointer"
                                 onClick={() => setLeafDataIndex(index, {cultivars: arrayRemoveIndex(leaf.data.cultivars, cultivarIndex)})}>
                                x
                            </div>
                        </div>)}
                    </div>
                </td>
                <td className={classNames("table-data", leaf.data.delete ? 'inactive' : '')}>
                    <Input
                        onPaste={e => pasteInColumn(e, index, v => ({growthStage: v}))}
                        className={leaf.rules.growthPhase  ? '' : 'border-1 border-red-600'}
                        value={leaf.data.growthStage} 
                        change={v => setLeafDataIndex(index, {growthStage: v})}/>
                </td>
                <td>
                    <GenericSelect
                        className={leaf.rules.test ? '' : 'border-1 border-red-600'}
                        options={props.data.tests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={leaf.data.test}
                        onChange={v => setLeafDataIndex(index, {test: v as TestType})}/>
                </td>
                <td>
                    <GenericSelect
                        options={props.data.optionalTests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={leaf.data.optionalTests || []}
                        multiple={true}
                        onChange={v => {
                            setLeafDataIndex(index, {optionalTests: v as TestType[]})
                        }}
                    />
                </td>

                <td className="table-data">
                    {leaf.data.delete ? <div className="btn-sm btn-primary"
                                        onClick={() => setLeafDataIndex(index, {delete: false})}>+</div> : null}
                    {!leaf.data.delete ? <div className="btn-sm btn-error"
                                         onClick={() => setLeafDataIndex(index, {delete: true})}>-</div> : null}
                    {leaf.data.testDataId === null ? (
                        <div className="btn-sm btn-error" onClick={() => removeLeaf(props.leafs, index)}>
                            x
                        </div>
                    ) : null}
                </td>
            </tr>)}
        </tbody>
    </table>
    </div>
}

export default SubmissionTable;